import * as Sentry from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';

import {
  APP_INITIALIZER,
  ENVIRONMENT_INITIALIZER,
  ErrorHandler,
  enableProdMode,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  Router,
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
} from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { CurrencyPipe } from '@angular/common';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { IonicStorageModule } from '@ionic/storage-angular';
import { importProvidersFrom } from '@angular/core';
import { provideTransloco, provideTranslocoTranspiler } from '@jsverse/transloco';
import { provideLottieOptions } from 'ngx-lottie';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { TranslocoHttpLoader } from './app/core/translation/transloco-loader';
import { TranslocoCustomTranspiler } from './app/core/translation/transloco-custom-transpiler';
import { TranslationService } from './app/core/services/translation/translation.service';
import { AuthInterceptor } from './app/core/interceptors/auth.interceptor';
import { InitService } from './app/core/services/init/init.service';
import { SingularService } from './app/core/services/singular/singular.service';

import { environment } from './environments/environment';
import config from 'capacitor.config';

// Preload modules to prevent loading chunk failed error when offline
import '@ionic/core/dist/esm/ion-alert.entry.js';
import '@ionic/core/dist/esm/ion-loading.entry.js';
import '@ionic/core/dist/esm/ion-modal.entry.js';
import '@ionic/core/dist/esm/ion-toast.entry.js';
import '@ionic/core/dist/esm/ion-action-sheet.entry.js';
import '@ionic/core/dist/esm/ion-popover.entry.js';

if (environment.production) {
  enableProdMode();
}

Sentry.init(
  {
    dsn: 'https://a47b5c1c4a5a213c9b653b8e1314eedb@o4508852223737856.ingest.us.sentry.io/4509207334223872',
    release: `clinch-app@${config.plugins?.['CapacitorUpdater']?.['version']}`,
    environment: environment.production ? 'production' : 'sandbox',
    dist: `${environment.build}`,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: environment.production ? 0.05 : 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/play\.clinch\.gg\//],
    sendDefaultPii: true,
    debug: false,
  },
  // Forward the init method from @sentry/angular
  SentryAngular.init,
);

export function initializeTranslation(translationService: TranslationService) {
  return () => translationService.preloadLanguage();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideIonicAngular({ rippleEffect: true, mode: 'md' }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideAnimations(),
    importProvidersFrom(IonicStorageModule.forRoot()),
    provideTransloco({
      config: {
        availableLangs: ['en', 'pt_BR', 'fi', 'fr', 'de', 'es', 'ru'],
        defaultLang: 'en',
        fallbackLang: 'en',
        missingHandler: {
          useFallbackTranslation: true,
        },
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoTranspiler(TranslocoCustomTranspiler),
    provideLottieOptions({
      // The light player drops expression support, which no thumbnail uses
      player: () => import('lottie-web/build/player/lottie_light'),
    }),
    { provide: ErrorHandler, useValue: SentryAngular.createErrorHandler() },
    {
      provide: SentryAngular.TraceService,
      deps: [Router],
    },
    TranslationService,
    CurrencyPipe,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslation,
      deps: [TranslationService, SentryAngular.TraceService],
      multi: true,
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      useValue: () => {
        inject(InitService);
        inject(SingularService);
      },
      multi: true,
    },
  ],
}).catch((err) => console.log(err));
